import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setToken, getToken, getTrustedDeviceToken, setTrustedDeviceToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const login = useCallback(async (email, password, totp_code, opts = {}) => {
    setError("");
    try {
      const payload = { email, password };
      if (totp_code) payload.totp_code = totp_code;
      const tdt = getTrustedDeviceToken();
      if (tdt) payload.trusted_device_token = tdt;
      if (opts.remember_device) {
        payload.remember_device = true;
        payload.device_label = opts.device_label || defaultDeviceLabel();
      }
      const { data } = await api.post("/auth/login", payload);
      if (data?.requires_2fa) {
        return "2fa";
      }
      if (data?.token) setToken(data.token);
      if (data?.trusted_device_token) setTrustedDeviceToken(data.trusted_device_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role });
      return true;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    // NOTE: we intentionally keep the trusted-device token so the user
    // doesn't need to re-enter 2FA on the next login from the same device.
    setUser(false);
  }, []);

  const updateUser = useCallback((patch) => {
    setUser((prev) => (prev && typeof prev === "object") ? { ...prev, ...patch } : prev);
  }, []);

  return (
    <AuthContext.Provider value={{ user, error, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function defaultDeviceLabel() {
  try {
    const ua = navigator.userAgent || "";
    let os = "Sconosciuto";
    if (/Windows/i.test(ua)) os = "Windows";
    else if (/Mac/i.test(ua)) os = "macOS";
    else if (/Linux/i.test(ua)) os = "Linux";
    else if (/Android/i.test(ua)) os = "Android";
    else if (/iPhone|iPad|iOS/i.test(ua)) os = "iOS";
    let browser = "Browser";
    if (/Edg\//i.test(ua)) browser = "Edge";
    else if (/Chrome\//i.test(ua)) browser = "Chrome";
    else if (/Firefox\//i.test(ua)) browser = "Firefox";
    else if (/Safari\//i.test(ua)) browser = "Safari";
    return `${browser} · ${os}`;
  } catch {
    return "Dispositivo";
  }
}

export const useAuth = () => useContext(AuthContext);
